import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Wrench, 
  ArrowLeft,
  Calendar as CalendarIcon,
  Car,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingPage = () => {
  const { mechanicId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [mechanic, setMechanic] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(null);

  const [formData, setFormData] = useState({
    service_type: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    description: '',
    preferred_time: ''
  });

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'
  ];

  useEffect(() => {
    fetchData();
  }, [mechanicId]);

  const fetchData = async () => {
    try {
      const [mechanicRes, servicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/mechanics/${mechanicId}`),
        axios.get(`${API_URL}/api/services`)
      ]);
      setMechanic(mechanicRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load mechanic data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!date) {
      toast.error('Please select a date');
      return;
    }

    if (!formData.preferred_time) {
      toast.error('Please select a time');
      return;
    }

    setSubmitting(true);

    try {
      const bookingData = {
        mechanic_id: mechanicId,
        service_type: formData.service_type,
        vehicle_make: formData.vehicle_make,
        vehicle_model: formData.vehicle_model,
        vehicle_year: parseInt(formData.vehicle_year),
        description: formData.description,
        preferred_date: format(date, 'yyyy-MM-dd'),
        preferred_time: formData.preferred_time
      };

      await axios.post(`${API_URL}/api/bookings`, bookingData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      toast.success('Booking submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!mechanic) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <Wrench className="w-12 h-12 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Mechanic not found</h2>
        <Button onClick={() => navigate('/mechanics')}>Back to Mechanics</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to={`/mechanics/${mechanicId}`} className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6" data-testid="back-link">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Mechanic
        </Link>

        <Card className="bg-white border border-slate-200" data-testid="booking-form-card">
          <CardHeader>
            <CardTitle className="font-['Barlow'] text-2xl">Book a Service</CardTitle>
            <p className="text-slate-600">with Verified Mechanic</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Service Type */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Service Type *</Label>
                <Select 
                  value={formData.service_type} 
                  onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                >
                  <SelectTrigger className="h-11" data-testid="service-select">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.name}>
                        {service.name} - ${service.base_price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicle Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Make *</Label>
                  <Input
                    name="vehicle_make"
                    placeholder="Toyota"
                    value={formData.vehicle_make}
                    onChange={handleChange}
                    className="h-11"
                    required
                    data-testid="vehicle-make-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Model *</Label>
                  <Input
                    name="vehicle_model"
                    placeholder="Camry"
                    value={formData.vehicle_model}
                    onChange={handleChange}
                    className="h-11"
                    required
                    data-testid="vehicle-model-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Year *</Label>
                  <Input
                    name="vehicle_year"
                    type="number"
                    placeholder="2020"
                    min="1990"
                    max={new Date().getFullYear() + 1}
                    value={formData.vehicle_year}
                    onChange={handleChange}
                    className="h-11"
                    required
                    data-testid="vehicle-year-input"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Describe the Issue *</Label>
                <Textarea
                  name="description"
                  placeholder="Please describe what's wrong with your vehicle or what service you need..."
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  required
                  data-testid="description-input"
                />
              </div>

              {/* Date & Time */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Preferred Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full h-11 justify-start text-left font-normal"
                        data-testid="date-picker-trigger"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Preferred Time *</Label>
                  <Select 
                    value={formData.preferred_time} 
                    onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="time-select">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                  disabled={submitting}
                  data-testid="submit-booking-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Booking Request'}
                </Button>
                <p className="text-center text-sm text-slate-500 mt-3">
                  The mechanic will confirm your booking shortly
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BookingPage;
